/**
 * The family's versions — v2's `FamilyVersionsPanel`, on the skill editor's
 * version rows. Newest first; the current one is marked; clicking another
 * restores it. Restoring puts that version's code and specification back and
 * rebuilds nothing (v2) — the note above the list says so, so nobody expects
 * the RFA in Revit to have changed.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { LoaderIcon } from "../components/skills/icons";
import type { FamilyVersionList } from "./types";
import type { FamilyBuilderController } from "./useFamilyBuilder";

type VersionsState = { kind: "loading" } | { kind: "error"; message: string } | { kind: "loaded"; list: FamilyVersionList };

export function FamilyBuilderVersions({ controller }: { controller: FamilyBuilderController }) {
  const { t, locale } = useLocale();
  const [state, setState] = useState<VersionsState>({ kind: "loading" });
  const { client, artifactId, versionsKey } = controller;

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    void client
      .versions(artifactId)
      .then((list) => {
        if (!cancelled) setState({ kind: "loaded", list });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [client, artifactId, versionsKey]);

  const current = controller.view?.version;
  const blocked = controller.isBusy || controller.restoringVersion !== null;

  return (
    <section className="family-builder-versions">
      <div className="family-builder-panel-head">
        <h3>{t("skillEdit.versionsTitle")}</h3>
      </div>
      <p className="family-builder-versions-note">{t("familyBuilder.restoreNote")}</p>
      <div className="family-builder-versions-scroll">
        {state.kind === "loading" ? (
          <div className="skills-center muted">
            <LoaderIcon size={14} />
          </div>
        ) : state.kind === "error" ? (
          <p className="settings-msg err">{state.message}</p>
        ) : (
          <ul className="version-rows">
            {[...state.list.versions].reverse().map((entry) => {
              const isCurrent = entry.version === current;
              const disabled = isCurrent || blocked;
              const restore = () => {
                if (!disabled) void controller.restoreVersion(entry.version);
              };
              return (
                <li
                  key={entry.sha || entry.version}
                  className={`version-item${isCurrent ? " is-current" : ""}${disabled && !isCurrent ? " is-blocked" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-disabled={disabled}
                  onClick={restore}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") restore();
                  }}
                >
                  <div className="version-item-head">
                    <span className="version-item-main">
                      <span
                        className={`version-dot${isCurrent ? " is-current" : ""}`}
                        {...(isCurrent
                          ? { role: "img", "aria-label": t("skillVersions.current"), title: t("skillVersions.current") }
                          : { "aria-hidden": true })}
                      />
                      <span className="version-item-label">v{entry.version}</span>
                    </span>
                    <span className="version-item-date">
                      {controller.restoringVersion === entry.version ? <LoaderIcon size={11} /> : null}
                      {formatDate(entry.authoredAt, locale)}
                    </span>
                  </div>
                  {entry.message ? <p className="version-item-msg">{entry.message}</p> : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function formatDate(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(locale === "zh" ? "zh-TW" : "en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
